import type { PreflightCheck, PreflightResult } from '@avslack/shared';
import { huddlePreflight } from '../lib/api';
import { isUnreachable } from '../data/outbox';
import { tidyDevices } from './devices';

/**
 * Eloellenorzes csatlakozas ELOTT.
 *
 * Ez a valasz arra a panaszra, hogy "nem lehet elinditani a huddle-t":
 * ott a felulet elkezd porogni, es vagy lesz belole valami, vagy nem. Itt
 * elobb megnezzuk mind a harom feltetelt, es ha valamelyik hianyzik, KONKRET
 * mondatot mutatunk - nem "hiba tortent"-et.
 *
 * A jogosultsag-keres is itt tortenik: a felhasznalo akkor lat bongeszo-
 * ablakot, amikor szamit ra, nem a csatlakozas kozepen.
 */
export async function runPreflight(): Promise<PreflightResult & { unreachable?: boolean }> {
  const checks: PreflightCheck[] = [];
  let inputDevices: { deviceId: string; label: string }[] = [];
  let stream: MediaStream | null = null;
  /** Az AVSlack szerver maga nem erheto el (nincs kapcsolat) - lasd HuddleProvider `join`. */
  let unreachable = false;

  // 1. Mikrofon-engedely
  if (!navigator.mediaDevices?.getUserMedia) {
    checks.push({
      id: 'microphone',
      ok: false,
      detail:
        'Ez a böngésző nem támogatja a mikrofon-használatot. Nem biztonságos (http) kapcsolatnál is előfordul.',
    });
  } else {
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      checks.push({ id: 'microphone', ok: true, detail: null });
    } catch (error) {
      checks.push({ id: 'microphone', ok: false, detail: describeMicError(error) });
    }
  }

  // 2. Eszkozlista - a nevek csak megadott engedely utan latszanak, ezert ez a sorrend.
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    inputDevices = tidyDevices(
      devices
        .filter((d) => d.kind === 'audioinput')
        .map((d, index) => ({
          deviceId: d.deviceId,
          label: d.label || `Mikrofon ${index + 1}`,
        })),
    );

    checks.push({
      id: 'devices',
      ok: inputDevices.length > 0,
      detail:
        inputDevices.length > 0
          ? null
          : 'Nem található mikrofon. Csatlakoztass egyet, vagy nézd meg a rendszer hangbeállításait.',
    });
  } catch {
    checks.push({ id: 'devices', ok: false, detail: 'Az eszközök listázása nem sikerült.' });
  }

  // 3. Media-szerver elerhetosege
  try {
    const server = await huddlePreflight().catch((error: unknown) => {
      // CSAK az AVSlack szerver elerhetetlensege "nincs kapcsolat" - a media-
      // szerver probainak hibaja mast jelent (lent).
      unreachable = isUnreachable(error);
      throw error;
    });
    if (!server.ok || !server.url) {
      checks.push({ id: 'server', ok: server.ok, detail: server.detail });
    } else {
      // A szerver sajat valasza csak a FELE a kepnek - lasd `probeMediaServer`.
      const reachable = await probeMediaServer(server.url);
      // A WebSocket-emelest KULON nezzuk: a HTTPS atmehet ugy is, hogy a
      // WebSocket nem - lasd `probeMediaSocket`.
      checks.push({
        id: 'server',
        ...(reachable.ok ? await probeMediaSocket(server.url) : reachable),
      });
    }
  } catch {
    checks.push({
      id: 'server',
      ok: false,
      detail: 'Nem sikerült elérni az AVSlack szervert. Ellenőrizd a hálózati kapcsolatot.',
    });
  }

  // A probastream nem kell tovabb - a LiveKit sajat streamet nyit.
  // Enelkul a mikrofon-jelzes bekapcsolva maradna a fulon.
  stream?.getTracks().forEach((track) => track.stop());

  return { ok: checks.every((c) => c.ok), checks, inputDevices, unreachable };
}

/**
 * A media-szerver ellenorzese ONNAN, AHONNAN szamit: a bongeszobol.
 *
 * A szerver sajat ellenorzese a BELSO cimen megy (`http://livekit:7880`), a
 * bongeszo viszont a publikusat hasznalja. Ez ket kulon utvonal, es ebben a
 * projektben mar ketszer eltert egymastol (MinIO, LiveKit): a szerver zoldet
 * mutatott, mikozben a bongeszo nem ert oda. Egy eloellenorzes, ami nem azt az
 * utat meri, amit a hivas hasznal, rosszabb a semminel - zoldet mutat, aztan a
 * csatlakozas 15 masodperc utan elhasal egy ertelmezhetetlen hibaval.
 *
 * A LiveKit `/rtc/validate` vegpontja token nelkul 401-et ad. Barmilyen valasz
 * JO HIR: azt jelenti, odaertunk. Ha viszont a sajat alkalmazasunk valaszol
 * (HTML), akkor a cimrol hianyzik az utvonal - a kliens egy weboldalra nyitna
 * WebSocketet, es pontosan ez adja a "room connection has timed out" hibat.
 */
async function probeMediaServer(url: string): Promise<{ ok: boolean; detail: string | null }> {
  const base = url.replace(/^ws/, 'http').replace(/\/+$/, '');

  try {
    const response = await fetch(`${base}/rtc/validate`, {
      // Rovid hatarido: ez egy ellenorzes, nem varakoztathatja meg a hivast.
      signal: AbortSignal.timeout(6_000),
    });

    if ((response.headers.get('content-type') ?? '').includes('text/html')) {
      return {
        ok: false,
        detail:
          `A média-szerver címén az AVSlack maga válaszol, nem a média-szerver (${base}). ` +
          'A LIVEKIT_URL beállítás hibás — szólj az üzemeltetésnek.',
      };
    }

    return { ok: true, detail: null };
  } catch (error) {
    const reason =
      error instanceof DOMException && error.name === 'TimeoutError'
        ? 'nem válaszolt időben'
        : 'nem érhető el';
    return {
      ok: false,
      detail:
        `A média-szerver ${reason} innen: ${base}. ` +
        'Ha máshonnan működik, a hálózat vagy egy végpontvédelem blokkolja.',
    };
  }
}

/**
 * A WebSocket-emeles ellenorzese - kulon a HTTPS-tol.
 *
 * A hivas jelzese WebSocketen megy. Egy proxy vagy vegpontvedelem atengedheti
 * a sima HTTPS kerest, es kozben elnyelheti az emelest - ilyenkor a HTTPS
 * ellenorzes zoldet mutat, a csatlakozas viszont 15 masodperc utan elhal egy
 * "room connection has timed out" hibaval. Innen nezve ez a ket eset
 * megkulonboztethetetlen, ezert merjuk kulon.
 *
 * Token nelkul a LiveKit 401-gyel elutasit. A bongeszo ilyenkor GYORSAN hibat
 * jelez - es ez JO HIR: azt jelenti, hogy az emeles eljutott a media-szerverig.
 * Ha viszont semmi nem tortenik, akkor senki nem valaszol: valami utkozben
 * elnyeli a kapcsolatot.
 */
async function probeMediaSocket(url: string): Promise<{ ok: boolean; detail: string | null }> {
  const base = url.replace(/^http/, 'ws').replace(/\/+$/, '');

  if (await socketAnswers(`${base}/rtc`)) return { ok: true, detail: null };

  /*
    Ha a media-szerver nem valaszol, megnezzuk az AVSlack SAJAT WebSocketjet is.

    A ketto kulon valaszt ad: ha az sem valaszol, akkor ezen a gepen altalaban
    nem mennek at a WebSocket-kapcsolatok - es akkor nem csak a hivas romlik el,
    hanem az azonnali uzenet-ertesites es a behivas csorgese is. Ez a
    kulonbseg donti el, hogy a halozatot vagy egyetlen utvonalat kell nezni.
  */
  const ownSocketWorks = await socketAnswers(ownRealtimeUrl());

  return {
    ok: false,
    detail: ownSocketWorks
      ? `A média-szerver nem válaszol a hívás csatornáján (${base}). ` +
        'Az AVSlack saját valós idejű kapcsolata működik, tehát nem minden ' +
        'WebSocket akad el — a hálózat vagy a végpontvédelem célzottan ezt az ' +
        'útvonalat blokkolja.'
      : 'Ezen a gépen a WebSocket-kapcsolatok nem jutnak át — az AVSlack saját ' +
        'valós idejű kapcsolata sem. Emiatt nem csak a hívás nem indul: az ' +
        'azonnali értesítés és a behívás csörgése sem működik. Jellemzően ' +
        'vállalati tűzfal vagy végpontvédelem okozza.',
  };
}

/** Az AVSlack sajat valos ideju vegpontja - ugyanaz az origin, mas utvonal. */
function ownRealtimeUrl(): string {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws`;
}

/**
 * Valaszol-e barmit egy WebSocket-cim?
 *
 * NEM a sikeres belepest merjuk: token nelkul mindket vegpont elutasit. A
 * kerdes csak az, hogy megjon-e barmilyen valasz. Ha igen, az emeles atjutott;
 * ha semmi nem tortenik, valami utkozben elnyelte a kapcsolatot.
 */
function socketAnswers(url: string, timeoutMs = 6_000): Promise<boolean> {
  return new Promise((resolve) => {
    let socket: WebSocket;
    try {
      socket = new WebSocket(url);
    } catch {
      resolve(false);
      return;
    }

    const finish = (answered: boolean) => {
      clearTimeout(timer);
      try {
        socket.close();
      } catch {
        // A mar lezart kapcsolat zarasa nem hiba.
      }
      resolve(answered);
    };

    const timer = setTimeout(() => finish(false), timeoutMs);

    // Barmelyik esemeny azt jelenti, hogy VALAKI valaszolt - ennyi kell.
    socket.onerror = () => finish(true);
    socket.onclose = () => finish(true);
    socket.onopen = () => finish(true);
  });
}

/** A bongeszo hibaja onmagaban semmitmondo - forditsuk le hasznalhato mondatra. */
function describeMicError(error: unknown): string {
  const name = error instanceof DOMException ? error.name : '';

  switch (name) {
    case 'NotAllowedError':
      return 'A mikrofon használata le van tiltva ezen az oldalon. A címsor melletti lakat ikonnal engedélyezhető.';
    case 'NotFoundError':
      return 'Nem található mikrofon a gépen.';
    case 'NotReadableError':
      return 'A mikrofont már használja egy másik program. Zárd be (pl. Teams, Zoom), es próbáld újra.';
    case 'OverconstrainedError':
      return 'A kiválasztott mikrofon nem érhető el. Válassz másikat.';
    case 'SecurityError':
      return 'A böngésző biztonsági okból tiltja a mikrofont. HTTPS kapcsolat szükséges.';
    default:
      return error instanceof Error && error.message
        ? `A mikrofon nem érhető el: ${error.message}`
        : 'A mikrofon nem érhető el.';
  }
}

export const PREFLIGHT_LABELS: Record<PreflightCheck['id'], string> = {
  microphone: 'Mikrofon-engedély',
  devices: 'Hangeszközök',
  server: 'Média-szerver',
};
